import gql from 'graphql-tag';
import React, {ReactNode} from 'react';

import {assert} from 'Asserts';
import {ChartNode} from 'charts/ChartTypes';
import {GetChart, GetChartVariables} from 'charts/network/__generated/GetChart';
import {GET_CHART_QUERY} from 'charts/network/GetChart';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import {GetChartViewDataFrame, GetChartViewDataFrameVariables} from './__generated/GetChartViewDataFrame';

// For some cases (e.g. CHART_VIEW) we may query a data frame that represents a subset of output the data
// frame. As a result we query numRows sepearately, which enables us to request the content from a pre-sampled
// data frame and get the total number of rows from the unsampled data frame.
export const GET_CHART_VIEW_DATA_FRAME = gql`
  query GetChartViewDataFrame(
    $dataFrameId: StrictString!
    $fullDataFrameId: StrictString!
    $limit: StrictInt!
  ) @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    dataFrame(id: $dataFrameId) {
      ... on DataFrameNode {
        id
        dataFrameJson(limitOffsetInput: {offset: 0, limit: $limit})
        allColumnsJson
        randomSubsetDataFrameJson(limit: $limit)
      }
      ...InaccessibleInfoGraphQL
    }
    fullDataFrameInfo: dataFrame(id: $fullDataFrameId) {
      ... on DataFrameNode {
        id
        numRows
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface QueryingChartAndDataFrameProps {
  inputDataFrameId: string;
  fullDataFrameId: string;
  chartId?: string | null;
  children: (response: {
    dataFrame: GetChartViewDataFrame['dataFrame'];
    numRows: number;
    chart: ChartNode | null;
  }) => ReactNode;
  renderLoading: () => ReactNode;
}

export default function QueryingChartAndDataFrame({
  inputDataFrameId,
  fullDataFrameId,
  chartId,
  children,
  renderLoading,
}: QueryingChartAndDataFrameProps): JSX.Element {
  const dashboardQueryResponse = useBenchlingQuery<GetChartViewDataFrame, GetChartViewDataFrameVariables>(
    GET_CHART_VIEW_DATA_FRAME,
    {
      variables: {
        dataFrameId: inputDataFrameId,
        fullDataFrameId,
        limit: DEFAULT_DATA_FRAME_ROW_LIMIT,
      },
    }
  );
  const chartQueryResponse = useSkippableBenchlingQuery<GetChart, GetChartVariables>(GET_CHART_QUERY, {
    variables: {chartId: chartId!},
    skip: chartId == null,
  });

  if (
    dashboardQueryResponse.type === QueryResultType.SUCCESS &&
    (chartQueryResponse.type === QueryResultType.SUCCESS ||
      chartQueryResponse.type === QueryResultType.SKIPPED)
  ) {
    const {dataFrame, fullDataFrameInfo} = dashboardQueryResponse.data;

    assert(!isInaccessible(dataFrame), 'User does not have access to input data frame');
    assert(!isInaccessible(fullDataFrameInfo), 'User does not have access to input data frame');

    if (chartQueryResponse.type === QueryResultType.SUCCESS) {
      const {chart} = chartQueryResponse.data;

      assert(!isInaccessible(chart), 'User does not have access to output chart');

      return <>{children({dataFrame, numRows: fullDataFrameInfo.numRows, chart})}</>;
    }

    return <>{children({dataFrame, numRows: fullDataFrameInfo.numRows, chart: null})}</>;
  } else if (
    dashboardQueryResponse.type === QueryResultType.PENDING ||
    chartQueryResponse.type === QueryResultType.PENDING
  ) {
    return <>{renderLoading()}</>;
  }

  return (
    <div>
      <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load chart" />
    </div>
  );
}
